import React, { useContext, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import { imgRegex, whiteSpaceEmptyStrRegex } from "../../configs/App.Regex";
import { Formio } from "@formio/react";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import { RxReload } from "react-icons/rx";
import { LuImageOff } from "react-icons/lu";
import CfBreadcrumb from "../../common/CfBreadcrumb/CfBreadcrumb";
import GenerateCertificate from "../../service/GenerateCertificate";
import AuthContext from "../../store/auth-context";
import SettingOption from "../../components/SettingOption/SettingOption";
import { hasFormDataAttributes } from "../../utils/formUtils/hasFormDataAttributes";

const CertificateDetails = () => {
  const navigate = useNavigate();

  const { credential_template_id } = useParams();
  const [certificateTitle, setCertificateTitle] = useState("");
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [selectedProgrammeCode, setSelectedProgrammeCode] = useState("");
  const [error, setError] = useState("");
  const [programmeNameError, setProgrammeNameError] = useState("");
  const [batchNameError, setBatchNameError] = useState("");
  const [fieldsChanged, setFieldsChanged] = useState({});

  const [currentSVG, setCurrentSVG] = useState("");
  const [certificateData, setCertificateData] = useState();
  const [orgLogo, setOrgLogo] = useState("");
  const [customFieldChanged, setCustomFieldChanged] = useState({});
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState({});
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [verificationCost, setVerificationCost] = useState("");
  const [selectedComponents, setSelectedComponents] = useState([]);
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [programmeList, setProgrammeList] = useState([]);
  const [programmeNext, setProgrammeNext] = useState(null);
  const [programmeLoading, setProgrammeLoading] = useState(false);
  const [programmeDropdownOpen, setProgrammeDropdownOpen] = useState(false);
  const [programmeOffset, setProgrammeOffset] = useState(0);
  const programmeDropdownRef = useRef(null);
  const [batchList, setBatchList] = useState([]);
  const [batchNext, setBatchNext] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const [batchOffset, setBatchOffset] = useState(0);
  const [hasFormFields, setHasFormFields] = useState(false);
  const [toggleMessage, setToggleMessage] = useState("");
  const [isInvalidCombination, setIsInvalidCombination] = useState(false);
  const batchDropdownRef = useRef(null);

  const breadcrumbItems = [
    { title: "Edit", to: `/certificate/edit/${credential_template_id}` },
  ];

  const renderToContainer = (containerId, content) => {
    const container = document.getElementById(containerId);
    if (container) {
      const root = createRoot(container);
      root.render(content);
      return root;
    }
    return null;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handlePaymentCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    localStorage.setItem("verCheck", isChecked);
    setIsPaymentRequired(isChecked);

    if (!isChecked) {
      localStorage.removeItem("verCost");
      setVerificationCost("");
    }
  };

  const handleVerificationCostChange = (e) => {
    const rupees = e.target.value; // Keep it as a string
    const paisa = Math.round(parseFloat(rupees || 0) * 100); // Convert safely
    localStorage.setItem("verCost", paisa);
    setVerificationCost(rupees); // Keep rupees as a string for input
  };

  const fetchProgrammes = async (loadMore = false) => {
    try {
      setProgrammeLoading(true);
      const offset = loadMore ? programmeOffset : 0;
      const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.data) {
        if (loadMore) {
          setProgrammeList((prev) => [...prev, ...data.data.results]);
        } else {
          setProgrammeList(data.data.results);
        }
        setProgrammeNext(data.data.next);
        setProgrammeOffset(offset + data.data.results.length);
      }
    } catch (error) {
      console.error("Error fetching programmes:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch programmes",
      });
    } finally {
      setProgrammeLoading(false);
    }
  };

  const handleProgrammeScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      programmeNext &&
      !programmeLoading
    ) {
      fetchProgrammes(true);
    }
  };

  const fetchBatches = async (loadMore = false) => {
    if (!selectedProgrammeCode) return;

    try {
      setBatchLoading(true);
      const offset = loadMore ? batchOffset : 0;
      const endpoint = `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${selectedProgrammeCode}&limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.data) {
        if (loadMore) {
          setBatchList((prev) => [...prev, ...data.data.results]);
        } else {
          setBatchList(data.data.results);
        }
        setBatchNext(data.data.next);
        setBatchOffset(offset + data.data.results.length);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch batches",
      });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleProgrammeSelect = (programme) => {
    setProgrammeName(programme.programme_name);
    localStorage.setItem("programme_code", programme.programme_code);
    setBatchName("");
    localStorage.setItem("batch_code", "");
    setProgrammeDropdownOpen(false);
  };

  const handleBatchSelect = (batch) => {
    setBatchName(batch.batch_name);
    localStorage.setItem("batch_code", batch.batch_code);
    setBatchDropdownOpen(false);
  };

  const handleBatchScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      batchNext &&
      !batchLoading
    ) {
      fetchBatches(true);
    }
  };

  const submitCertificateData = async (submission, isPreview = false) => {
    const formData = { ...submission };

    // Handle logo
    if (!formData.data.logo || formData.data.logo.length === 0) {
      if (orgLogo) {
        formData.data.logo = [
          {
            storage: "base64",
            name: "org_logo.svg",
            url: orgLogo,
            type: "image/svg+xml",
          },
        ];
      } else {
        try {
          const orgResponse = await GET(EntityConstants.GET_ORG_DETAILS);
          const orgData = await orgResponse.json();
          if (orgData.org_logo) {
            formData.data.logo = [
              {
                storage: "base64",
                name: "default_org_logo.svg",
                url: orgData.org_logo,
                type: "image/svg+xml",
              },
            ];
          }
        } catch (err) {
          if (!isPreview) {
            Toast({
              type: "error",
              title: "Error",
              message: "API request failed. Please retry.",
            });
          }
          return;
        }
      }
    }

    // Validate required fields
    const certTitle = sessionStorage.getItem("certificate_title");
    const progName = sessionStorage.getItem("programme_name");
    const batchNm = sessionStorage.getItem("batch_name");
    const isCertTitleEmpty =
      !certTitle || certTitle === "" || whiteSpaceEmptyStrRegex.test(certTitle);
    const isProgrammeNameEmpty =
      !progName || progName === "" || whiteSpaceEmptyStrRegex.test(progName);
    const isBatchNameEmpty =
      !batchNm || batchNm === "" || whiteSpaceEmptyStrRegex.test(batchNm);

    const isVerificationAmountRequired =
      localStorage.getItem("verCheck") === "true";
    const isVerificationAmountEmpty =
      isVerificationAmountRequired &&
      (!localStorage.getItem("verCost") ||
        localStorage.getItem("verCost").trim() === "");

    if (
      isCertTitleEmpty ||
      isProgrammeNameEmpty ||
      isBatchNameEmpty ||
      isVerificationAmountEmpty
    ) {
      Toast({
        type: "error",
        title: "Error",
        message: "Please fill all the required fields.",
      });
      return;
    }

    const payload = {
      form_data: formData,
      credential_template_id: credential_template_id,
      certificate_name: sessionStorage.getItem("certificate_title"),
      programme_code: localStorage.getItem("programme_code") || "",
      batch_code: localStorage.getItem("batch_code") || "",
      certificate_redirect_url:
        sessionStorage.getItem("certificate_redirect_url") || undefined,
      is_auth_required:
        sessionStorage.getItem("is_auth_required") !== null
          ? JSON.parse(sessionStorage.getItem("is_auth_required"))
          : undefined,
      is_public:
        sessionStorage.getItem("is_public") !== null
          ? JSON.parse(sessionStorage.getItem("is_public"))
          : undefined,
      is_payment_required: localStorage.getItem("verCheck") === "true",
      verification_cost: localStorage.getItem("verCheck")
        ? localStorage.getItem("verCost")
        : null,
      verification_fields: {
        components: selectedComponents.filter((comp) => comp.selected),
      },
    };

    try {
      setLoading(true);
      const response = await POST(
        `${EntityConstants.POST_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
        payload
      );

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(
          "issuer_credential_template_id",
          data.issuer_credential_template_id
        );
        sessionStorage.setItem("details_form_data", JSON.stringify(submission));

        navigate(
          `/issuer/certificate/issue/${data.credential_template_id}/${data.issuer_credential_template_id}`
        );

        Toast({
          type: "success",
          title: "Success",
          message: "Certificate Template Saved Successfully",
        });
        return data;
      } else {
        const errorData = await response.json();
        if (errorData.message && Array.isArray(errorData.message)) {
          errorData.message.forEach((msg) => {
            Toast({
              type: "error",
              title: "Error",
              message: msg,
            });
          });
        } else {
          Toast({
            type: "error",
            title: "Error",
            message: "API request failed\nPlease retry...",
          });
        }
        return null;
      }
    } catch (error) {
      console.error("An error occurred:", error);
      Toast({
        type: "error",
        title: "Error",
        message: error.message || "API request failed",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    localStorage.setItem("advanced_settings_submit", "true");

    if (formRef.current) {
      const form = formRef.current;
      try {
        const submission = await form.submit();
        await submitCertificateData(submission);
      } catch (err) {
        console.error("Form submission error:", err);
      } finally {
        localStorage.removeItem("advanced_settings_submit");
      }
    }
  };

  const onSubmitHandle = async (sub, bool) => {
    if (bool) {
      await submitCertificateData(sub, true);
    } else {
      authContext.setTriggerLoginModuleToggleValue(true);
    }
  };

  const GET_CERTIFICATE_DATA = (credential_template_id) => {
    GET(
      `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`
    )
      .then((response) => response.json())
      .then((data) => {
        setCertificateData(data);
        setFormData(data?.default_template_variables_data?.data || {});
      });
  };

  const GET_TEMPLATE_FORM_DATA = async (credential_template_id) => {
    try {
      const certificateResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`
      );
      const certificateData = await certificateResponse.json();

      if (certificateData?.form_io_url) {
        const formComponentsResponse = await fetch(certificateData.form_io_url);
        const formComponents = await formComponentsResponse.json();
        const hasFields = hasFormDataAttributes(formComponents);
        setHasFormFields(hasFields);
      } else {
        setHasFormFields(false);
      }
    } catch (error) {
      console.error("Error loading template form data:", error);
      setHasFormFields(false);
    }
  };

  const GET_VERIFICATION_SETTINGS = async () => {
    try {
      const response = await GET(EntityConstants.POST_VERIFICATION_SETTINGS);
      if (!response.ok) {
        throw new Error("Failed to fetch verification settings");
      }
      const data = await response.json();

      // Store values in localStorage
      localStorage.setItem(
        "verCheck",
        JSON.stringify(data.is_payment_required_for_vc)
      );
      localStorage.setItem(
        "verCost",
        JSON.stringify(data.verification_cost_vc)
      );

      setIsPaymentRequired(data.is_payment_required_for_vc);
      setVerificationCost(String(data.verification_cost_vc / 100 || "")); // Convert to rupees and ensure it's a string
    } catch (error) {
      console.error("Error fetching verification settings:", error);
    }
  };

  const formFieldsChanged = (fields) => {
    if (fields?.data) {
      setFormData(fields.data);
      setFieldsChanged(fields.data);

      if (fields.data.logo && fields.data.logo[0]?.url) {
        setOrgLogo(fields.data.logo[0].url);
      }
    }
    if (
      fields.changed?.component?.key !== undefined &&
      fields.changed?.value !== undefined
    ) {
      const fieldKey = fields.changed.component.key;
      if (fieldKey === "logo") {
        const logoValue = fields.changed.value;
        if (!logoValue || logoValue.length === 0 || !logoValue[0]?.url) {
          setOrgLogo(orgLogo);
        } else if (imgRegex.test(logoValue[0].url)) {
          // If the logo is valid, update the orgLogo state
          setOrgLogo(logoValue[0].url);
        }
      }
      if (
        fields.changed?.value[0]?.type &&
        imgRegex.test(fields.changed?.value[0].url)
      ) {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }));
      } else {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }));
      }
    } else if (fields?.data) {
      setFieldsChanged(fields.data);
    }
  };

  useEffect(() => {
    credential_template_id && GET_CERTIFICATE_DATA(credential_template_id);
    credential_template_id && GET_TEMPLATE_FORM_DATA(credential_template_id);
    GET_VERIFICATION_SETTINGS();
  }, [credential_template_id]);

  const fetchSvg = async (certificateData) => {
    const { template_variables_data, template_svg } = certificateData;
    try {
      const response = await fetch(`${template_svg}`);
      // const response = await fetch(`${template_svg}`);
      if (response.ok) {
        const svgContent = await response.text();
        setCurrentSVG(svgContent);
        setFieldsChanged(template_variables_data);
        //!injecting the svg content into the container
        const container = document.getElementById("certificate_container");
        container.style.filter = "drop-shadow(0 0 2px rgba(0, 0, 0, 0.264))";
        container.innerHTML = svgContent;
      } else if (response.status === 404) {
        renderToContainer(
          "certificate_container",
          <div className="text-black flex justify-center w-full h-full text-lg">
            <div className="flex flex-col items-center gap-2 w-fit h-fit">
              <span className="flex items-center gap-2 w-fit h-fit  bg-gray-100 px-2 py-1 rounded-lg">
                <p>Couldn&apos;t find image..</p>
                <LuImageOff className="text-cfPrimaryCardHover " />
              </span>
              <span>
                <p>Please create template again...</p>
              </span>
            </div>
          </div>
        );
      } else {
        renderToContainer(
          "certificate_container",
          <div className="text-black flex justify-center w-full h-full text-lg">
            <div
              className="flex items-center gap-2 w-fit h-fit rounded-lg cursor-pointer"
              onClick={() => {
                renderToContainer(
                  "certificate_container",
                  <span className="w-full flex justify-center">
                    <CfLoader />
                  </span>
                );
                certificateData && fetchSvg(certificateData);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  renderToContainer(
                    "certificate_container",
                    <span className="w-full flex justify-center">
                      <CfLoader />
                    </span>
                  );
                  certificateData && fetchSvg(certificateData);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <p>Couldn&apos;t Load Image...</p>
              <RxReload className="text-cfPrimaryCardHover " />
            </div>
          </div>
        );
      }
    } catch (error) {
      console.error("Error loading SVG:", error);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("batch_code");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("batch_code");
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        programmeDropdownRef.current &&
        !programmeDropdownRef.current.contains(event.target)
      ) {
        setProgrammeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        batchDropdownRef.current &&
        !batchDropdownRef.current.contains(event.target)
      ) {
        setBatchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (authContext.isLoggedIn) {
      GET(EntityConstants.GET_ORG_DETAILS)
        .then((response) => response.json())
        .then((data) => {
          setOrgLogo(data.org_logo);
        })
        .catch((error) => {
          console.log("Error in GET_ORG_DETAILS : ", error);
          Toast({
            type: "error",
            title: "Error",
            message: "API request failed...",
          });
        });
    }
  }, [authContext.isLoggedIn]);

  useEffect(() => {
    if (
      certificateData &&
      typeof certificateData === "object" &&
      Object.prototype.hasOwnProperty.call(
        certificateData,
        "default_template_variables_data"
      )
    ) {
      fetchSvg(certificateData);
    }
  }, [certificateData]);

  useEffect(() => {
    if (certificateData) {
      setProgrammeName(certificateData.programme_name || "");
      setBatchName(certificateData.batch_name || "");
      localStorage.setItem(
        "programme_code",
        certificateData.programme_code || ""
      );
      localStorage.setItem("batch_code", certificateData.batch_code || "");
    }
  }, [certificateData]);

  useEffect(() => {
    if (currentSVG) {
      const updatedFields = isToggleOn
        ? { ...fieldsChanged, logo: orgLogo }
        : { ...customFieldChanged, logo: orgLogo };

      const modifiedSVG = GenerateCertificate(currentSVG, updatedFields);
      document.getElementById("certificate_container").innerHTML = modifiedSVG;
    }
  }, [fieldsChanged, customFieldChanged, currentSVG, orgLogo, isToggleOn]);

  useEffect(() => {
    if (certificateTitle.trim().length === 0) {
      setError("Certificate Name is required");
      sessionStorage.removeItem("certificate_title");
    } else {
      sessionStorage.setItem("certificate_title", certificateTitle);
      setError("");
    }
  }, [certificateTitle]);

  useEffect(() => {
    if (programmeName.trim().length === 0) {
      setProgrammeNameError("Programme Name is required");
      sessionStorage.removeItem("programme_name");
      localStorage.removeItem("programme_code");
    } else {
      sessionStorage.setItem("programme_name", programmeName);
      setProgrammeNameError("");

      const programme = programmeList.find(
        (p) => p.programme_name === programmeName
      );
      if (programme) {
        localStorage.setItem("programme_code", programme.programme_code);
      }
    }
  }, [programmeName, programmeList]);

  useEffect(() => {
    if (batchName.trim().length === 0) {
      setBatchNameError("Batch Name is required");
      sessionStorage.removeItem("batch_name");
      localStorage.removeItem("batch_code");
    } else {
      sessionStorage.setItem("batch_name", batchName);
      setBatchNameError("");

      const batch = batchList.find((b) => b.batch_name === batchName);
      if (batch) {
        localStorage.setItem("batch_code", batch.batch_code);
      }
    }
  }, [batchName, batchList]);

  useEffect(() => {
    sessionStorage.setItem("is_auth_required", isAuthRequired);
  }, [isAuthRequired]);

  useEffect(() => {
    sessionStorage.setItem("is_public", isPublic);
  }, [isPublic]);

  useEffect(() => {
    fetchProgrammes();
  }, []);

  useEffect(() => {
    if (!batchDropdownOpen) {
      setBatchOffset(0);
    }
  }, [batchDropdownOpen]);

  useEffect(() => {
    if (selectedProgrammeCode) {
      fetchBatches();
    }
  }, [selectedProgrammeCode]);

  useEffect(() => {
    if (programmeName && programmeList.length > 0) {
      const selectedProgramme = programmeList.find(
        (programme) => programme.programme_name === programmeName
      );
      if (selectedProgramme) {
        setSelectedProgrammeCode(selectedProgramme.programme_code);
      }
    }
  }, [programmeName, programmeList]);

  useEffect(() => {
    if (certificateData?.form_io_url) {
      const historyFormData = sessionStorage.getItem("details_form_data");

      if (historyFormData) {
        setIsToggleOn(false);
        fetch(certificateData?.form_io_url)
          .then((resp) => resp.json())
          .then((data) => {
            certificateData?.form_io_url &&
              Formio.createForm(
                document.getElementById("formio"),
                data,
                {}
              ).then((form) => {
                formRef.current = form;
                form.ready.then(() => {
                  form.submission = {
                    data: { ...JSON.parse(historyFormData).data },
                  };
                  form.on("change", formFieldsChanged);
                  form.on("submit", (sub) => {
                    if (
                      localStorage.getItem("advanced_settings_submit") ===
                      "true"
                    ) {
                      localStorage.removeItem("advanced_settings_submit");
                      return;
                    }
                    onSubmitHandle(sub, authContext.isLoggedIn);
                  });
                });
              });
            sessionStorage.removeItem("issuer_credential_template_id");
          })
          .catch((error) =>
            console.log("Error while hitting formio url: ", error)
          );
      } else {
        fetch(certificateData?.form_io_url)
          .then((resp) => resp.json())
          .then((data) => {
            certificateData?.form_io_url &&
              Formio.createForm(
                document.getElementById("formio"),
                data,
                {}
              ).then((form) => {
                formRef.current = form;
                form.ready.then(() => {
                  form.submission = {
                    data: isToggleOn
                      ? {
                          ...certificateData?.default_template_variables_data
                            ?.data,
                        }
                      : { ...formData },
                  };
                  form.on("change", formFieldsChanged);
                  form.on("submit", (sub) => {
                    if (
                      localStorage.getItem("advanced_settings_submit") ===
                      "true"
                    ) {
                      localStorage.removeItem("advanced_settings_submit");
                      return;
                    }
                    onSubmitHandle(sub, authContext.isLoggedIn);
                  });
                });
              });
            sessionStorage.removeItem("issuer_credential_template_id");
          })
          .catch((error) =>
            console.log("Error while hitting formio url: ", error)
          );
      }
    }
  }, [
    certificateData?.form_io_url,
    authContext.isLoggedIn,
    isToggleOn,
    activeTab,
  ]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
    const historyCertfificateName = sessionStorage.getItem("certificate_title");
    const historyProgrammeName = sessionStorage.getItem("programme_name");
    const historyBatchName = sessionStorage.getItem("batch_name");
    if (historyCertfificateName) {
      setCertificateTitle(historyCertfificateName);
    }
    if (historyProgrammeName) {
      setProgrammeName(historyProgrammeName);
    }
    if (historyBatchName) {
      setBatchName(historyBatchName);
    }
  }, []);

  useEffect(() => {
    if (!programmeDropdownOpen) {
      setProgrammeOffset(0);
    }
  }, [programmeDropdownOpen]);

  const GET_SUBJECT_CERTIFICATE_DATA = async (credential_template_id) => {
    try {
      const formResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_SUBJECT_FORM}${credential_template_id}/subject?type=form`
      );
      const formData = await formResponse.json();

      const formComponentsResponse = await fetch(
        formData.credential_subject_form
      );
      const formComponents = await formComponentsResponse.json();

      const draftResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`
      );
      const draftData = await draftResponse.json();

      // Get the current verification fields or initialize empty array
      const currentVerificationFields =
        draftData.verification_fields?.components || [];
      const currentSelectedKeys = currentVerificationFields.map((c) => c.key);

      const filteredComponents = formComponents.components
        .filter((comp) => comp.key !== "submit")
        .map((comp) => ({
          ...comp,
          selected: currentSelectedKeys.includes(comp.key),
        }));

      setSelectedComponents(filteredComponents);
    } catch (error) {
      console.error("Error loading subject data:", error);
    }
  };

  useEffect(() => {
    GET_SUBJECT_CERTIFICATE_DATA(credential_template_id);
  }, []);

  const handleCheckboxChange = (componentKey) => {
    setSelectedComponents((prevComponents) =>
      prevComponents.map((comp) =>
        comp.key === componentKey ? { ...comp, selected: !comp.selected } : comp
      )
    );
  };

  const handleAuthRequiredChange = (e) => {
    const newAuthRequired = e.target.checked;
    if (newAuthRequired && isPublic) {
      Toast({
        type: "error",
        title: "Invalid Combination",
        message:
          "Cannot enable Authentication Required when Is Public is enabled. These options conflict.",
      });
      return;
    }
    setIsAuthRequired(newAuthRequired);
  };

  const handleIsPublicChange = (e) => {
    const newIsPublic = e.target.checked;
    if (newIsPublic && isAuthRequired) {
      Toast({
        type: "error",
        title: "Invalid Combination",
        message:
          "Cannot enable Is Public when Authentication Required is enabled. These options conflict.",
      });
      return;
    }
    setIsPublic(newIsPublic);
  };

  useEffect(() => {
    const validateToggleCombinations = () => {
      const hasAuth = isAuthRequired;
      const isPub = isPublic;
      const hasPayment = isPaymentRequired;

      const allDisabled = !hasPayment && !hasAuth && !isPub;
      if (allDisabled) {
        setIsInvalidCombination(false);
        setToggleMessage("");
        return;
      }

      if (hasAuth && isPub) {
        setIsInvalidCombination(true);
        setToggleMessage(
          "Invalid combination: 'Is Authentication Required' and 'Is Public' cannot both be enabled. Authentication prevents email delivery, while Public requires it."
        );
        return;
      }
      setIsInvalidCombination(false);

      const handleVerifyPortalClick = () =>
        window.open("https://verify.credissuer.com/", "_blank");
      const handleMyCertsClick = () =>
        window.open("https://mycerts.credissuer.com/", "_blank");

      if (hasPayment && hasAuth && isPub) {
        setToggleMessage(
          "Only 'Enable Paid Verification' will apply. The other two options conflict."
        );
      } else if (hasPayment && hasAuth) {
        setToggleMessage(
          <span>
            • The user will be asked to pay the verification fee through the{" "}
            <button
              className="font-bold text-[#3538CD] underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              verify-portal
            </button>
            .<br />• The certificate will not be shared directly via the email
            notification. The user must log in to{" "}
            <button
              className="font-bold text-[#3538CD] underline focus:outline-none rounded"
              onClick={handleMyCertsClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleMyCertsClick();
                }
              }}
              tabIndex={0}
            >
              My-Certs
            </button>{" "}
            and complete authentication to download it.
          </span>
        );
      } else if (hasPayment && isPub) {
        setToggleMessage(
          <span>
            • The certificate will be sent via email notification.
            <br />• And during the verification of the certificate, the user
            will be asked to pay the specified fee through the{" "}
            <button
              className="font-bold text-[#3538CD] underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              verify-portal
            </button>
            .
          </span>
        );
      } else if (hasAuth && isPub) {
        setToggleMessage("Invalid combination");
      } else if (hasPayment) {
        setToggleMessage(
          <span>
            If enabled, users must pay the set amount during verification in the{" "}
            <button
              className="font-bold text-[#3538CD] underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              verify-portal
            </button>{" "}
            for the verification.
          </span>
        );
      } else if (hasAuth) {
        setToggleMessage(
          <span>
            If enabled, the certificate is not sent via the email notification;
            users must authenticate and then download it from{" "}
            <button
              className="font-bold text-[#3538CD] underline focus:outline-none rounded"
              onClick={handleMyCertsClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleMyCertsClick();
                }
              }}
              tabIndex={0}
            >
              My-Certs
            </button>
            .
          </span>
        );
      } else if (isPub) {
        setToggleMessage(
          "If enabled, the certificate is sent directly via the email notification."
        );
      }
    };

    validateToggleCombinations();
  }, [isPaymentRequired, isAuthRequired, isPublic]);

  return (
    <>
      {breadcrumbItems.length > 0 && (
        <CfBreadcrumb
          breadcrumbItems={breadcrumbItems}
          isToggleOn={isToggleOn}
          setIsToggleOn={setIsToggleOn}
          marginTop={"mt-[0.5rem]"}
          isPostPaid={true}
          hasFormFields={hasFormFields}
        />
      )}

      <div className="flex w-full">
        <div className="flex flex-col w-full md:w-2/5 items-center flex-grow bg-cfSecondary h-screen overflow-y-auto">
          <div className="flex space-x-0 md:space-x-4 mb-2 mt-4 bg-gray-300 rounded-xl md:rounded-2xl p-1 mx-1 md:mx-0">
            <button
              className={`px-2 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "basic"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("basic")}
            >
              Basic Settings
            </button>
            <button
              className={`px-1 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "advanced"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("advanced")}
            >
              Advanced Settings
            </button>
          </div>

          <div className="w-full px-4 py-2">
            {activeTab === "basic" && (
              <div className="flex flex-col w-full items-start">
                <label
                  htmlFor="certificateName"
                  className="after:content-['*'] after:text-red-600"
                >
                  Certificate Name
                </label>
                <div className="flex justify-between items-center focus-within:border-4 focus-within:border-cfCertificateNameInputBorder rounded-xl w-full">
                  <div className="flex justify-between items-center border-[1px] rounded-md p-1 w-full">
                    <input
                      type="text"
                      name="certificateName"
                      className="border-none rounded-md w-full focus:outline-none"
                      id="certificateName"
                      onChange={(e) => {
                        const value = e.target.value;
                        setCertificateTitle(value);
                      }}
                      value={certificateTitle}
                    />
                  </div>
                </div>
                {error && <p className="text-red-600">{error}</p>}
                <label
                  htmlFor="programmeName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  Programme Name
                </label>
                <div className="relative w-full" ref={programmeDropdownRef}>
                  <div
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer"
                    onClick={() => {
                      setProgrammeDropdownOpen(!programmeDropdownOpen);
                      if (
                        !programmeDropdownOpen &&
                        programmeList.length === 0
                      ) {
                        fetchProgrammes();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setProgrammeDropdownOpen(!programmeDropdownOpen);
                        if (
                          !programmeDropdownOpen &&
                          programmeList.length === 0
                        ) {
                          fetchProgrammes();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <input
                      type="text"
                      name="programmeName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="programmeName"
                      value={programmeName}
                      readOnly
                      placeholder="Select a programme"
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        programmeDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {programmeDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleProgrammeScroll}
                    >
                      {programmeList.length > 0 ? (
                        programmeList.map((programme) => (
                          <div
                            key={programme.programme_code}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleProgrammeSelect(programme);
                              setProgrammeName(programme.programme_name);
                              setProgrammeDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleProgrammeSelect(programme);
                                setProgrammeName(programme.programme_name);
                                setProgrammeDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {programme.programme_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {programmeLoading
                            ? "Loading..."
                            : "No programmes found"}
                        </div>
                      )}
                      {programmeLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {programmeNameError && (
                  <p className="text-red-600">{programmeNameError}</p>
                )}

                {/* Add Batch Name field */}
                <label
                  htmlFor="batchName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  Batch Name
                </label>
                <div className="relative w-full" ref={batchDropdownRef}>
                  <div
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer"
                    onClick={() => {
                      if (!selectedProgrammeCode) {
                        Toast({
                          type: "warning",
                          title: "Warning",
                          message: "Please select a programme first",
                        });
                        return;
                      }
                      setBatchDropdownOpen(!batchDropdownOpen);
                      if (!batchDropdownOpen && batchList.length === 0) {
                        fetchBatches();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        if (!selectedProgrammeCode) {
                          Toast({
                            type: "warning",
                            title: "Warning",
                            message: "Please select a programme first",
                          });
                          return;
                        }
                        setBatchDropdownOpen(!batchDropdownOpen);
                        if (!batchDropdownOpen && batchList.length === 0) {
                          fetchBatches();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                      backgroundColor: !selectedProgrammeCode
                        ? "#f3f4f6"
                        : "inherit",
                      cursor: !selectedProgrammeCode
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <input
                      type="text"
                      name="batchName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="batchName"
                      value={batchName}
                      readOnly
                      placeholder={
                        !selectedProgrammeCode
                          ? "Select a programme first"
                          : "Select a batch"
                      }
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        batchDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {batchDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleBatchScroll}
                    >
                      {batchList.length > 0 ? (
                        batchList.map((batch) => (
                          <div
                            key={batch.batch_code}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleBatchSelect(batch);
                              setBatchName(batch.batch_name);
                              setBatchDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleBatchSelect(batch);
                                setBatchName(batch.batch_name);
                                setBatchDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {batch.batch_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {batchLoading ? "Loading..." : "No batches found"}
                        </div>
                      )}
                      {batchLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {batchNameError && (
                  <p className="text-red-600">{batchNameError}</p>
                )}

                <div id="formio" className="w-full p-2 h-full">
                  <div className="flex justify-center items-center">
                    <CfLoader />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div className="advanced-settings">
                <div className="flex flex-col mb-4 mt-2">
                  <div
                    htmlFor="verification-fields"
                    className="text-gray-800 font-semibold"
                  >
                    Certificate Delivery and Verification Settings
                  </div>
                  <div className="text-gray-600 text-sm">
                    These options control how end users receive and verify their
                    credentials after issuance.
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <SettingOption
                    key={"toggle_is_payment_required"}
                    toggleStatus={isPaymentRequired}
                    heading={"Enable paid verification"}
                    onclickFunction={handlePaymentCheckboxChange}
                    label={"Enable paid verification"}
                  />
                </div>
                {isPaymentRequired && (
                  <div className="mb-4">
                    <label htmlFor="price" className="text-gray-600">
                      Verification Amount to be paid
                    </label>
                    <div className="flex items-center rounded-md bg-white pl-3 outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600">
                      <div className="shrink-0 select-none text-base text-gray-500 sm:text-sm/6 mr-2.5">
                        ₹
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        min="0"
                        className="block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 border border-gray-500"
                        placeholder="0.00"
                        value={verificationCost}
                        onChange={handleVerificationCostChange}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {!verificationCost.trim() && (
                      <p className="text-red-600 mt-1">
                        Verification amount is required.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <SettingOption
                    key={"toggle_is_auth_required"}
                    toggleStatus={isAuthRequired}
                    heading={"Is Authentication Required"}
                    onclickFunction={handleAuthRequiredChange}
                    label={"Is Authentication Required"}
                    disabled={isPublic}
                  />
                </div>

                <div>
                  <SettingOption
                    key={"toggle_is_public"}
                    toggleStatus={isPublic}
                    heading={"Is Public"}
                    onclickFunction={handleIsPublicChange}
                    label={"Is Public"}
                    disabled={isAuthRequired}
                  />
                </div>

                {toggleMessage && (
                  <div
                    className={`mb-4 p-4 rounded-lg border-none ${
                      isInvalidCombination
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-blue-50 border-blue-200 text-[#3538CD]"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">Current Settings:</h4>
                    <div className="text-sm">
                      {typeof toggleMessage === "string" ? (
                        <div className="whitespace-pre-line">
                          {toggleMessage}
                        </div>
                      ) : (
                        toggleMessage
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex flex-col mb-4">
                    <div
                      htmlFor="verification-fields"
                      className="text-gray-800 font-semibold"
                    >
                      Verification Fields
                    </div>
                    <div className="text-gray-600 text-sm">
                      Choose which information should be required for users to
                      verify their certificates in the verification portal when
                      the QR code is unavailable.
                    </div>
                  </div>
                  <div
                    id="verification-fields"
                    className="max-h-60 overflow-y-auto border rounded p-2"
                  >
                    {selectedComponents.map((component) => (
                      <div
                        key={component.key}
                        className="flex items-center mb-2"
                      >
                        <input
                          type="checkbox"
                          checked={component.selected}
                          onChange={() => handleCheckboxChange(component.key)}
                          className="mr-2 rounded-lg"
                          id={`checkbox-${component.key}`}
                        />
                        <label htmlFor={`checkbox-${component.key}`}>
                          {component.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  className="submit-btn mt-6 px-4 py-2 bg-[#3538CD] text-cfSecondary w-full text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={handleSubmit}
                  disabled={loading || isInvalidCombination}
                >
                  {isInvalidCombination
                    ? "Please fix invalid settings"
                    : "Submit"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="w-full mx-1 p-3">
          <div className="w-full">
            <div
              className="flex flex-col flex-shrink max-h-[78vh]"
              id="certificate_container"
            >
              <CfLoader />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificateDetails;
